import MapboxDraw, { DrawCustomMode, DrawModeChangeEvent } from "@mapbox/mapbox-gl-draw";
import { Feature, Polygon } from "geojson";

import { simplify } from "fond/turf";

import { selectFeaturesInPolygon } from "./selectFeaturesInPolygon";

const {
  constants: Constants,
  lib: { doubleClickZoom },
} = MapboxDraw;

/** Minimum interval (ms) between coordinate additions during lasso drag */
const LASSO_THROTTLE_MS = 16;

/** Tolerance for turf simplify (~5.5m at equator) */
const LASSO_SIMPLIFY_TOLERANCE = 0.00005;

/** Minimum number of unique coordinates needed to form a valid lasso polygon */
const LASSO_MIN_POINTS = 3;

/** Source/layer ID for the lasso visual rendered outside MapboxDraw's pipeline */
const LASSO_SOURCE_ID = "mapbox-gl-draw-lasso";

/** Update the standalone lasso source with the current coordinates as a closed polygon */
function updateLassoSource(map: MapboxDraw.DrawCustomModeThis["map"], coords: number[][]) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const source = map.getSource(LASSO_SOURCE_ID) as any;
  if (!source) return;

  if (coords.length < 3) {
    // Not enough points for a polygon — show as a line
    source.setData({
      type: "Feature",
      properties: {},
      geometry: { type: "LineString", coordinates: coords },
    });
  } else {
    // Close the ring and render as a polygon
    source.setData({
      type: "Feature",
      properties: {},
      geometry: { type: "Polygon", coordinates: [[...coords, coords[0]]] },
    });
  }
}

/** Remove the standalone lasso source and layers from the map (idempotent) */
function cleanupLassoLayer(map: MapboxDraw.DrawCustomModeThis["map"]) {
  if (map.getLayer(`${LASSO_SOURCE_ID}-fill`)) map.removeLayer(`${LASSO_SOURCE_ID}-fill`);
  if (map.getLayer(`${LASSO_SOURCE_ID}-stroke`)) map.removeLayer(`${LASSO_SOURCE_ID}-stroke`);
  if (map.getSource(LASSO_SOURCE_ID)) map.removeSource(LASSO_SOURCE_ID);
}

/**
 * Custom draw mode for selecting features by drawing a freehand lasso.
 *
 * Click-drag to draw a freehand polygon, release to close and select all
 * features that fall within or intersect the drawn shape.
 *
 * The lasso visual is rendered via a standalone Mapbox GL source/layer,
 * bypassing MapboxDraw's render pipeline to avoid flicker.
 */
const lassoSelect: DrawCustomMode = {
  onSetup: function (opts) {
    this.clearSelectedFeatures();
    doubleClickZoom.disable(this);
    this.updateUIClasses({ mouse: Constants.cursors.ADD });
    this.setActionableState({ combineFeatures: false, uncombineFeatures: false, trash: true });

    // Clean up any leftover lasso layer from a previous session
    cleanupLassoLayer(this.map);

    // Add a standalone source/layers for the lasso visual
    this.map.addSource(LASSO_SOURCE_ID, {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });
    this.map.addLayer({
      id: `${LASSO_SOURCE_ID}-fill`,
      type: "fill",
      source: LASSO_SOURCE_ID,
      paint: {
        "fill-color": "#3bb2d0",
        "fill-opacity": 0.1,
      },
    });
    this.map.addLayer({
      id: `${LASSO_SOURCE_ID}-stroke`,
      type: "line",
      source: LASSO_SOURCE_ID,
      paint: {
        "line-color": "#3bb2d0",
        "line-width": 2,
        "line-dasharray": [2, 2],
      },
    });

    return {
      lassoCoords: [] as number[][],
      isLassoing: false,
      lastUpdateTime: 0,
      onDraw: opts.onDraw || ((polygon: Feature<Polygon>) => selectFeaturesInPolygon(this, polygon)),
    };
  },

  onClick: function () {
    // Clicks are ignored — drawing happens via drag
  },

  onMouseDown: function (state, e) {
    // Only respond to left-click
    if (e.originalEvent.button !== 0) return;

    state.isLassoing = true;
    state.lastUpdateTime = Date.now();
    state.lassoCoords = [];

    // Disable map panning while lassoing
    this.map.dragPan.disable();

    // Add the first coordinate
    const { lng, lat } = e.lngLat;
    state.lassoCoords.push([lng, lat]);
    updateLassoSource(this.map, state.lassoCoords);
  },

  onDrag: function (state, e) {
    if (!state.isLassoing) return;

    // Throttle coordinate additions
    const now = Date.now();
    if (now - state.lastUpdateTime < LASSO_THROTTLE_MS) return;

    state.lastUpdateTime = now;

    const { lng, lat } = e.lngLat;
    state.lassoCoords.push([lng, lat]);
    updateLassoSource(this.map, state.lassoCoords);
  },

  onMouseUp: function (state) {
    if (!state.isLassoing) return;

    state.isLassoing = false;
    this.map.dragPan.enable();
    cleanupLassoLayer(this.map);

    if (state.lassoCoords.length < LASSO_MIN_POINTS) {
      // Not enough points — cancel and re-enter lasso mode
      this.changeMode("lasso_select" as MapboxDraw.DrawMode);
      return;
    }

    // Build a closed polygon from the collected coordinates
    const closedCoords = [...state.lassoCoords, state.lassoCoords[0]];
    const rawPolygon: Feature<Polygon> = {
      type: "Feature",
      properties: {},
      geometry: { type: "Polygon", coordinates: [closedCoords] },
    };
    const simplified = simplify(rawPolygon, { tolerance: LASSO_SIMPLIFY_TOLERANCE, highQuality: false });

    // Verify the simplified polygon is still valid (at least 4 coords including closing point)
    const coords = simplified.geometry.coordinates[0];
    if (coords.length < 4) {
      this.changeMode("lasso_select" as MapboxDraw.DrawMode);
      return;
    }

    // Run the selection using the simplified polygon
    if (typeof state.onDraw === "function") {
      state.onDraw(simplified);
    }
  },

  onMouseMove: function () {
    this.updateUIClasses({ mouse: Constants.cursors.ADD });
  },

  onKeyUp: function (state, e) {
    if (e.keyCode === 27) {
      // ESC — cancel
      state.isLassoing = false;
      this.map.dragPan.enable();
      cleanupLassoLayer(this.map);
      this.changeMode(Constants.modes.SIMPLE_SELECT);
      this.map?.fire("draw.modechange", { type: "draw.modechange", mode: "simple_select" } as DrawModeChangeEvent);
    }
  },

  onStop: function () {
    this.map.dragPan.enable();
    this.updateUIClasses({ mouse: Constants.cursors.NONE });
    doubleClickZoom.enable(this);
    this.activateUIButton();
    cleanupLassoLayer(this.map);
  },

  toDisplayFeatures: function (state, geojson, display) {
    // Lasso visual is rendered via a standalone map layer — just pass through other features
    display(geojson);
  },
};

export default lassoSelect;
